import {
  CButton,
  CFormInput,
  CFormTextarea,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
  CModalTitle,
} from '@coreui/react-pro'
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Select from 'react-select'
import './SnippetSection.scss'
import SearchBoxEncounter from 'src/components/common/SearchBoxEncounter/SearchBoxEncounter'
import { onSnippetAdd } from 'src/redux/progressNote/progressNoteSlice'
import Highlighter from 'react-highlight-words'
import Button from 'src/components/common/Button/Button'
import { addMySnippets, editThisSnippets, sortAllSnippets } from 'src/helper/backendHelper'
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd'
import { userSettingPut } from '../../../redux/user/userThunk'
import { toast } from 'react-toastify'
import GenericConfirmModal from '../census/components/ConfirmationModals/GenericConfirmModal'
import { selectStyleDark, selectStyleLight } from '../../../utils/sharedCareTeamLogic'
import { getTheme } from '../../../redux/user/userSlice'

const SNIPPET_SORT_OPTIONS = [
  { label: 'Oldest', value: 'Oldest' },
  { label: 'Newest', value: 'Newest' },
  { label: 'A-Z', value: 'A-Z' },
  { label: 'Custom', value: 'Custom' },
]

function truncate(str, n) {
  return str.length > n ? str.slice(0, n - 1) + '....' : str
}

function sortSnippetsByType(sortType, snippets) {
  let sortedSnippets = []
  snippets = [...snippets]

  switch (sortType) {
    case 'Custom':
      sortedSnippets = snippets.sort((a, b) => a?.sort_value - b?.sort_value)
      break

    case 'A-Z':
      let sortedByTitle = snippets
        ?.filter((item) => item?.title)
        ?.sort((a, b) => a.title.localeCompare(b.title))
      let sortedBySnip = snippets
        ?.filter((item) => !item?.title)
        ?.sort((a, b) => a.snippets.localeCompare(b.snippets))

      sortedSnippets = [...sortedByTitle, ...sortedBySnip]
      break

    case 'Oldest':
      sortedSnippets = snippets.sort(
        (a, b) => new Date(a?.created_date).getTime() - new Date(b?.created_date).getTime(),
      )
      break

    case 'Newest':
      sortedSnippets = snippets.sort(
        (a, b) => new Date(b?.created_date).getTime() - new Date(a?.created_date).getTime(),
      )
      break

    default:
      sortedSnippets = snippets
      break
  }

  return sortedSnippets
}

const SnippetSection = ({
  onDeleteSnippet,
  setIsTyping,
  section,
  snippetList,
  setSnippetList,
  getSnips,
  setIsDraftSaved,
}) => {
  const dispatch = useDispatch()
  const userSettings = useSelector((state) => state.user.settings)
  const theme = useSelector(getTheme)

  const [searchText, setSearchText] = useState('') //state for user input in search bar
  const [showAllSnippets, setShowAllSnippets] = useState(userSettings?.showAllSnippets || false)
  const [filterSelected, setFilterSelected] = useState(userSettings?.snippetSort || 'Custom')

  const [isAddSnippetOpen, setIsAddSnippetOpen] = useState(false)
  const [isEditSnippetOpen, setIsEditSnippetOpen] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState(false)
  const [newSnippet, setNewSnippet] = useState('')
  const [newSnippetTitle, setNewSnippetTitle] = useState('')
  const [editSnippetData, setEditSnippetData] = useState()
  const [deleteSnippetId, setDeleteSnippetId] = useState(null)

  const [sortedSnippets, setSortedSnippets] = useState(
    sortSnippetsByType(filterSelected, snippetList),
  )

  const onUpdateSettings = (key, value) => {
    // ensure local state is updated before we save settings
    let newUserSettings = { ...userSettings, [key]: value }
    if (Object.keys(newUserSettings)?.length > 1) {
      dispatch(userSettingPut(newUserSettings))
    }
  }

  const updateSnippetFilter = (value) => {
    setFilterSelected(value)
    onUpdateSettings('snippetSort', value)
  }

  const onNoteChange = (value) => {
    setNewSnippet(value)
  }
  const onNoteChangeEdit = (value) => {
    setEditSnippetData(value)
  }

  useEffect(() => {
    let sortedSnippets = sortSnippetsByType(filterSelected, snippetList)
    setSortedSnippets(sortedSnippets)
  }, [filterSelected, snippetList])

  const onSelect = (txt) => {
    dispatch(onSnippetAdd([txt]))
    setIsTyping(true)
  }

  const onRemove = () => {
    let tempList = snippetList?.filter((snip) => snip?._id !== deleteSnippetId)
    setSortedSnippets(tempList)

    onDeleteSnippet(deleteSnippetId)
    setDeleteConfirm(!deleteConfirm)
    setDeleteSnippetId(null)
  }

  const onEditOpen = (value) => {
    setIsEditSnippetOpen(true)
    setEditSnippetData(value)
  }

  const onDelete = (id) => {
    setDeleteConfirm(true)
    setDeleteSnippetId(id)
  }

  const addSnippet = (snippetBody) => {
    if (!newSnippetTitle || !newSnippetTitle?.trim()) {
      toast.error('Snippet must have a title')
      return
    }
    if (!snippetBody || !snippetBody?.trim()) {
      toast.error('Snippet must be filled out')
      return
    }

    const trimTitle = newSnippetTitle?.trim()

    let alreadyInSnippets = snippetList?.find(
      (snip) => snip?.snippets === snippetBody || snip?.title === trimTitle,
    )

    if (alreadyInSnippets) {
      if (alreadyInSnippets?.snippets === snippetBody) {
        toast.error(`Snippet already exists`)
      } else if (alreadyInSnippets?.title === trimTitle) {
        toast.error(`Snippet header already exists`)
      }
    } else {
      let payload = { section: section?.title, snippets: snippetBody, title: trimTitle }
      addMySnippets(payload).then((res) => {
        if (res?.status === 'success') {
          toast.success(`Snippet added...`)
          setNewSnippetTitle('')
          setIsAddSnippetOpen(false)
          getSnips((results) => {
            return sortSnippetsByType(filterSelected, results)
          })
        }
      })
    }
  }

  const editMySnippet = (snippet) => {
    if (!snippet?.title) {
      toast.error('Snippet must have a title')
      return
    }
    if (!snippet?.snippets) {
      toast.error('Snippet must be filled out')
      return
    }

    let alreadyInSnippets = snippetList?.find(
      (snip) =>
        (snip?.snippets === snippet?.snippets || snip?.title === snippet?.title) &&
        snip?._id !== snippet?._id,
    )

    if (alreadyInSnippets) {
      if (alreadyInSnippets?.snippets === snippet?.snippets) {
        toast.error(`Snippet already exists`)
        return
      } else if (alreadyInSnippets?.title === snippet?.title) {
        toast.error(`Snippet header already exists`)
        return
      }
    }

    let payload = {
      section: section?.title,
      template_text: snippet?.snippets,
      title: editSnippetData?.title,
    }

    editThisSnippets(snippet?._id, payload).then((res) => {
      if (res?.status === 'success') {
        toast.success(`Snippet edited...`)
        setIsEditSnippetOpen(false)
        getSnips((results) => {
          return sortSnippetsByType(filterSelected, results)
        })
      }
    })
  }

  const onDragEnd = (result) => {
    if (!result.destination) {
      return
    }
    const content = reorder(sortedSnippets, result.source.index, result.destination.index)

    setSortedSnippets(content)
  }

  const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list)
    const [removed] = result.splice(startIndex, 1)
    result.splice(endIndex, 0, removed)
    sortSave(result)
    return result
  }

  const sortSave = (snippet) => {
    let snipPayload = snippet?.map((snip, index) => ({
      id: snip?._id,
      sort_value: index,
    }))

    sortAllSnippets({ snipet: snipPayload })

    setSnippetList(
      snippet?.map((item, i) => ({
        ...item,
        sort_value: i,
      })),
    )

    updateSnippetFilter('Custom')
  }

  useEffect(() => {
    if (isAddSnippetOpen) {
      setNewSnippet(sortedSnippets?.length === 0 ? searchText : '')
    }
  }, [isAddSnippetOpen, sortedSnippets, searchText])

  return (
    <>
      {section && (
        <div id={'SnippetSection'} className="filter-section-holder ">
          <div className="sort-holder">
            <Button compressed onClick={() => setIsAddSnippetOpen(true)}>
              Create Snippet
            </Button>
            <div className={'d-flex justify-content-between'}>
              <label className={'d-flex justify-content-center align-items-center me-3 gap-2'}>
                <input
                  type="checkbox"
                  checked={showAllSnippets}
                  onChange={(e) => {
                    setShowAllSnippets(e.target.checked)
                    onUpdateSettings('showAllSnippets', e.target.checked)
                  }}
                />
                Show All Snippets
              </label>
              <div className="sort-select" style={{ cursor: 'pointer' }}>
                <Select
                  aria-label="Sort Option"
                  className={'default-select-dropdown'}
                  styles={theme === 'light' ? selectStyleLight : selectStyleDark}
                  onChange={({ value }) => {
                    updateSnippetFilter(value)
                  }}
                  value={SNIPPET_SORT_OPTIONS?.find(({ value }) => value === filterSelected)}
                  options={SNIPPET_SORT_OPTIONS}
                  isSearchable={false}
                  menuShouldScrollIntoView={false}
                />
              </div>
            </div>
          </div>
          <div className="snippet-section-header">
            <div className="title">{section?.title}</div>
            <div className="snippet-search-holder mb-2">
              <SearchBoxEncounter
                searchText={searchText}
                placeholder={`Search snippet`}
                setSearchText={setSearchText}
              />
            </div>
          </div>

          {deleteConfirm && (
            <GenericConfirmModal
              onClose={() => {
                setDeleteConfirm(false)
                setDeleteSnippetId(null)
              }}
              onConfirm={onRemove}
              content={'Are you sure you want to delete this snippet?'}
            />
          )}

          {isAddSnippetOpen && (
            <CModal
              className={'confirm-modal'}
              visible={isAddSnippetOpen}
              size="lg"
              onClose={() => {
                setIsAddSnippetOpen(false)
                setNewSnippetTitle('')
              }}
              backdrop="static"
            >
              <CModalHeader
                onClose={() => {
                  setIsAddSnippetOpen(false)
                  setNewSnippetTitle('')
                }}
              >
                <CModalTitle>New Snippet</CModalTitle>
              </CModalHeader>
              <CModalBody className="snippet-form">
                <CFormInput
                  className="form-control input-box mb-2"
                  value={newSnippetTitle}
                  placeholder={'Snippet Title*'}
                  type="text"
                  onChange={(e) => setNewSnippetTitle(e?.currentTarget?.value)}
                ></CFormInput>

                <CFormTextarea
                  type="textarea"
                  placeholder="Type to add snippets*"
                  value={newSnippet}
                  rows="8"
                  onChange={(e) => onNoteChange(e?.currentTarget?.value)}
                />
              </CModalBody>
              <CModalFooter>
                <CButton
                  color="secondary"
                  onClick={() => {
                    setIsAddSnippetOpen(false)
                    setNewSnippetTitle('')
                  }}
                >
                  Close
                </CButton>
                <CButton
                  color="primary"
                  disabled={!newSnippetTitle || !newSnippet}
                  onClick={() => {
                    addSnippet(newSnippet)
                  }}
                >
                  Save Snippet
                </CButton>
              </CModalFooter>
            </CModal>
          )}

          {isEditSnippetOpen && (
            <CModal
              className={'confirm-modal'}
              visible={isEditSnippetOpen}
              size="lg"
              onClose={() => {
                setIsEditSnippetOpen(false)
              }}
              backdrop="static"
            >
              <CModalHeader
                onClose={() => {
                  setIsEditSnippetOpen(false)
                }}
              >
                <CModalTitle>Edit Snippet</CModalTitle>
              </CModalHeader>
              <CModalBody className="snippet-form">
                <CFormInput
                  className="form-control input-box mb-2"
                  value={editSnippetData?.title}
                  placeholder={'Snippet Title*'}
                  type="text"
                  onChange={(e) =>
                    onNoteChangeEdit({ ...editSnippetData, title: e?.currentTarget?.value })
                  }
                ></CFormInput>
                <CFormTextarea
                  type="textarea"
                  placeholder="Type to add snippets*"
                  value={editSnippetData?.snippets}
                  rows="8"
                  onChange={(e) =>
                    onNoteChangeEdit({ ...editSnippetData, snippets: e?.currentTarget?.value })
                  }
                />
              </CModalBody>
              <CModalFooter>
                <CButton color="secondary" onClick={() => setIsEditSnippetOpen(false)}>
                  Close
                </CButton>
                <CButton
                  color="primary"
                  disabled={!editSnippetData?.title || !editSnippetData?.snippets}
                  onClick={() => editMySnippet(editSnippetData)}
                >
                  Save Snippet
                </CButton>
              </CModalFooter>
            </CModal>
          )}

          <div className="section-item-holder snippet-scroll">
            <div className="section-filter" onClick={() => setIsTyping(false)}>
              <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="snippets">
                  {(provided) => (
                    <div ref={provided.innerRef}>
                      {sortedSnippets
                        ?.filter((snip) => {
                          if (showAllSnippets) {
                            return true
                          }
                          return snip?.section === section?.title
                        })
                        ?.filter(
                          (snip) =>
                            snip?.snippets?.toLowerCase()?.includes(searchText?.toLowerCase()) ||
                            snip?.title?.toLowerCase()?.includes(searchText?.toLowerCase()),
                        )
                        ?.map((item, index) => (
                          <Draggable key={item?._id} draggableId={item?._id} index={index}>
                            {(provided) => (
                              <div
                                className="drag-item"
                                key={index}
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                              >
                                <SnippetItem
                                  onSelect={onSelect}
                                  searchText={searchText}
                                  item={item}
                                  setIsDraftSaved={setIsDraftSaved}
                                  onEditOpen={onEditOpen}
                                  onDelete={onDelete}
                                />
                              </div>
                            )}
                          </Draggable>
                        ))}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

const SnippetItem = ({ onSelect, searchText, item, onEditOpen, onDelete, setIsDraftSaved }) => {
  const [isViewMore, setIsViewMore] = useState(false)

  return (
    <div className="filter-check-box txt-pre">
      <div className="snip-holder">
        <button
          className="icon-button"
          onClick={(e) => {
            onSelect(item?.snippets)
            setIsDraftSaved(false)
            e.stopPropagation()
          }}
        >
          <i className="fa-solid fa-circle-plus fa-inverse"></i>
        </button>
        <div className="sni-txt">
          {item?.title && (
            <b>
              <p>
                <Highlighter
                  highlightClassName="highlight"
                  searchWords={searchText ? [searchText] : []}
                  autoEscape={true}
                  textToHighlight={item?.title ? item?.title : ''}
                />
              </p>
            </b>
          )}

          <Highlighter
            highlightClassName="highlight"
            searchWords={[searchText]}
            autoEscape={true}
            textToHighlight={isViewMore ? item?.snippets : truncate(item?.snippets, 250)}
          />
        </div>
      </div>

      <div className="del-holder">
        <button className="del-btn" onClick={() => onEditOpen(item)}>
          <i className="fa-solid fa-pencil"></i>
        </button>
        <button className="del-btn" onClick={() => onDelete(item?._id)}>
          <i className="fa-solid fa-trash"></i>
        </button>
        {item?.snippets?.length > 250 && (
          <button className="del-btn" onClick={() => setIsViewMore(!isViewMore)}>
            {isViewMore ? (
              <i className="fa-solid fa-chevron-up"></i>
            ) : (
              <i className="fa-solid fa-chevron-down"></i>
            )}
          </button>
        )}
      </div>
    </div>
  )
}

export default SnippetSection
